import {
  Button,
  ButtonGroup,
  FormLabel,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useState, ChangeEvent } from 'react';
import { quickbooksDesktopExport } from '../../../../api/oms/order-history';
import { IAlert } from '../../../../models/common';
import { IPHRequest } from '../../../../models/purchase-history';
import {
  TQBRadioButton,
  TQBRenderFlag,
} from '../../../../models/purchase-history/quick-books-export';
import { pushErrorEvent } from '../../../../utils/analytics';
import { createTimeStamp } from '../../../../utils/date-utils';
import { openFile, parseBlob } from '../../../../utils/http-helper';
import { pushQuickBookExportEvent } from '../../../../utils/purchase-history/analytics';

interface IQuickBooksDesktopExport {
  setQBRenderFlag: (value: TQBRenderFlag) => void;
  request: IPHRequest;
  setOpenModal: (value: boolean) => void;
  setAlertState: (value: IAlert) => void;
}

const saveQBFile = (data: string, fileName: string): void => {
  const blob = parseBlob(data, 'data:application/iif');
  return openFile(blob, fileName);
};

const handleSuccessDesktopRequest = (
  response: AxiosResponse,
  setOpenModal: (value: boolean) => void,
  setAlertState: (value: IAlert) => void,
  fileName: string,
): void => {
  const msg = `Purchase History Data successfully exported. 
    Please check your downloads folder for the file.`;

  setAlertState({ status: 'success', alertMessage: msg });
  saveQBFile(response.data, `${fileName}.iif`);
  setOpenModal(false);
};

const handleFailureOnlineRequest = (
  error: AxiosError,
  setAlertState: (value: IAlert) => void,
  setOpenModal: (value: boolean) => void,
) => {
  pushErrorEvent(
    'purchase history quickbooks desktop export',
    error.code || '',
    error.message,
    'inline',
  );
  setAlertState({
    status: 'error',
    alertMessage: 'We ran into a problem, please try again.',
  });
  setOpenModal(false);
};

const handleOnlineExportClick = (
  request: IPHRequest,
  qbExportValue: TQBRadioButton,
  setOpenModal: (value: boolean) => void,
  setAlertState: (value: IAlert) => void,
  fileName: string,
  userId: string,
) => {
  const updatedRequest = request;
  if (qbExportValue === 'details') {
    updatedRequest.includeDetails = true;
  }
  const wrappedRequest = { orderHistoryRequest: updatedRequest };
  pushQuickBookExportEvent(
    `purchase history: quickbooks export desktop ${qbExportValue}`,
  );
  quickbooksDesktopExport(wrappedRequest, userId)
    .then((response: AxiosResponse) => {
      handleSuccessDesktopRequest(
        response,
        setOpenModal,
        setAlertState,
        fileName,
      );
    })
    .catch((error: AxiosError) => {
      handleFailureOnlineRequest(error, setAlertState, setOpenModal);
    });
};

const QuickBooksDesktopExport = ({
  setQBRenderFlag,
  request,
  setOpenModal,
  setAlertState,
}: IQuickBooksDesktopExport) => {
  const { userID } = useThdCustomer() || {};
  const [radioValue, setRadioValue] = useState<TQBRadioButton>();
  const [fileName, setFileName] = useState<string>(
    `Purchase_History_${createTimeStamp()}`,
  );

  return (
    <>
      <ModalBody>
        <>
          <FormLabel id="online-qb-export-radio-buttons-group-label">
            Select data to export:
          </FormLabel>
          <RadioGroup
            aria-labelledby="online-qb-export-radio-buttons-group-label"
            name="online-qb-export-radio-buttons-group"
            value={radioValue}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setRadioValue(event.target.value as TQBRadioButton);
            }}
          >
            <SelectionControlLabel value="summary" label="Summary Data">
              <Radio />
            </SelectionControlLabel>
            <SelectionControlLabel value="details" label="Details Data">
              <Radio />
            </SelectionControlLabel>
          </RadioGroup>
        </>
        <div className="sui-py-1">
          <Typography>Data will be exported as an iif file.</Typography>
        </div>

        <TextField
          required
          fullWidth
          label="Filename"
          placeholder="Default File Name"
          statusMessage={fileName === '' ? 'Filename is required' : undefined}
          status={fileName === '' ? 'error' : undefined}
          value={fileName}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setFileName(event.target.value);
          }}
        />
      </ModalBody>
      <ModalFooter position="end">
        <ButtonGroup>
          <Button
            onClick={() => {
              setQBRenderFlag(undefined);
            }}
          >
            Go Back
          </Button>
          <Button
            disabled={
              radioValue === undefined && (fileName !== '' || undefined)
            }
            onClick={() =>
              handleOnlineExportClick(
                request,
                radioValue,
                setOpenModal,
                setAlertState,
                fileName,
                userID,
              )
            }
          >
            Submit
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </>
  );
};

export default QuickBooksDesktopExport;
